import { ompApiClient } from "@app/config/axios/clients/ompApiClient";
import type { RequestConfig } from "@app/config/axios/http";
import type {
    ApiDefineWsInplantNumReturns,
    ApiWsInplant,
    ApiWsInplantContent,
    ApiWsInplantContentFilter,
    ApiWsInplantFilter,
} from "./types";

interface WsInplantsListResponse {
    wsInplants: ApiWsInplant[];
}

interface WsInplantsContentsListResponse {
    wsInplantContents: ApiWsInplantContent[];
}

interface WsInplantMutationResponse {
    outData: ApiWsInplant;
}

interface WsInplantContentMutationResponse {
    outData: ApiWsInplantContent;
}

interface GetNewWsInplantObjResponse {
    newWsInplantObj: ApiWsInplant;
}

interface DefineWsInplantNumberResponse {
    result: ApiDefineWsInplantNumReturns;
}

/**
 * Транспортный слой OMP — ВЗН УП: заголовок и состав (`content`).
 */
export const wsInplantsApi = {
    /**
     * Загружает документы ВЗН по фильтру.
     * Запрос уходит в endpoint `stock/wsInplants.loadByFilter`.
     */
    load: async (
        params: {
            flt?: ApiWsInplantFilter;
        },
        config?: RequestConfig,
    ): Promise<ApiWsInplant[]> => {
        const data = await ompApiClient.postOmega<WsInplantsListResponse>("stock/wsInplants.loadByFilter", params, config);
        return data.wsInplants;
    },

    /** Создает новый документ ВЗН через endpoint `stock/wsInplants.insert`. */
    insert: async (data: ApiWsInplant, config?: RequestConfig): Promise<ApiWsInplant> => {
        const result = await ompApiClient.postOmega<WsInplantMutationResponse>("stock/wsInplants.insert", { data }, config);
        return result.outData;
    },

    /** Возвращает серверный шаблон нового документа ВЗН. */
    getNew: async (config?: RequestConfig): Promise<ApiWsInplant> => {
        const response = await ompApiClient.postOmega<GetNewWsInplantObjResponse>(
            "stock/wsInplants.getNewWsInplantObj",
            {},
            config,
        );
        return response.newWsInplantObj;
    },

    /** Генерирует номер документа ВЗН по текущим данным заголовка. */
    defineNumber: async (params: { data: ApiWsInplant }, config?: RequestConfig): Promise<ApiDefineWsInplantNumReturns> => {
        const response = await ompApiClient.postOmega<DefineWsInplantNumberResponse>(
            "stock/wsInplants.generateNum",
            params,
            config,
        );
        return response.result;
    },

    content: {
        /** Загружает строки состава ВЗН по фильтру. */
        load: async (
            params: {
                flt?: ApiWsInplantContentFilter;
            },
            config?: RequestConfig,
        ): Promise<ApiWsInplantContent[]> => {
            const data = await ompApiClient.postOmega<WsInplantsContentsListResponse>(
                "stock/wsInplants/contents.loadByFilter",
                params,
                config,
            );
            return data.wsInplantContents;
        },

        /** Добавляет строку состава документа ВЗН. */
        insert: async (data: ApiWsInplantContent, config?: RequestConfig): Promise<ApiWsInplantContent> => {
            const result = await ompApiClient.postOmega<WsInplantContentMutationResponse>(
                "stock/wsInplants/contents.insert",
                { data },
                config,
            );
            return result.outData;
        },

        /** Обновляет строку состава документа ВЗН. */
        update: async (data: ApiWsInplantContent, config?: RequestConfig): Promise<ApiWsInplantContent> => {
            const result = await ompApiClient.postOmega<WsInplantContentMutationResponse>(
                "stock/wsInplants/contents.update",
                { data },
                config,
            );
            return result.outData;
        },

        /** Удаляет строку состава документа ВЗН по коду. */
        delete: async (params: { code: number }, config?: RequestConfig): Promise<void> => {
            await ompApiClient.postOmega("stock/wsInplants/contents.remove", params, config);
        },
    },
} as const;
